'use client';
import {
  Popover,
  PopoverContent,
  PopoverHandler,
} from '@material-tailwind/react';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BaseTranslations } from '../../../baseTranslations';
import { useCmsClientContext } from '../../../utils/cmsPageContext';
import { getClientTranslations } from '../../../utils/translations/getClientTranslations';
import { MaterialIcon } from '../../MaterialIcon';

export interface OptionProps {
  value: string;
  label: string;
}

export interface SelectProps {
  placeholder?: string;
  options: OptionProps[];
  prefixIcon?: React.ReactNode;
  onChange?: (value: string) => void;
  value?: string;
  border?: boolean;
  search?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  labelClassName?: string;
  containerClassName?: string;
  showBodyShadow?: boolean;
  showDropdownArrow?: boolean;
  openedContainerClassName?: string;
  dropdownClassName?: string;
  disabledClassName?: string;
  popoverClassName?: string;
}

export const Select: React.FC<SelectProps> = ({
  prefixIcon,
  options,
  value,
  placeholder,
  border,
  onChange,
  search,
  disabled = false,
  isLoading = false,
  containerClassName,
  openedContainerClassName,
  labelClassName,
  showBodyShadow = false,
  showDropdownArrow = true,
  dropdownClassName = '',
  disabledClassName = '',
  popoverClassName = '',
}) => {
  const [isPopoverOpened, setIsPopoverOpened] = useState<boolean>(false);
  const [optionsToDisplay, setOptionsToDisplay] =
    useState<OptionProps[]>(options);
  const [selectedOptionLabel, setSelectedOptionLabel] = useState<
    string | undefined
  >('');
  const [searchInputValue, setSearchInputValue] = useState<string>('');

  const popoverHandlerRef = useRef<HTMLDivElement | null>(null);
  const popoverSearchFieldRef = useRef<HTMLInputElement | null>(null);

  const disabledStyles =
    disabledClassName || '!cursor-default pointer-events-none !bg-gray-500';

  const togglePopover = () => {
    if (!disabled && !isLoading) {
      setIsPopoverOpened((prev) => !prev);
    }
  };

  const handleSearchInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const searchValue = event.target.value;

    const filteredOptions = options.filter(
      (option) =>
        option.value.toLowerCase().includes(searchValue.toLowerCase()) ||
        option.label.toLowerCase().includes(searchValue.toLowerCase()),
    );

    setSearchInputValue(searchValue);
    setOptionsToDisplay(filteredOptions);
  };

  const handleChange = (option: OptionProps) => {
    setSelectedOptionLabel(option.label);
    onChange?.(option.value);
    setIsPopoverOpened(false);
  };

  const findDefaultValueLabel = useCallback(() => {
    const selectedOption = options.filter(
      (option) => option.value === value,
    )[0];
    if (selectedOption) {
      setSelectedOptionLabel(selectedOption.label);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const focusPopoverSearchField = useCallback(() => {
    if (isPopoverOpened && popoverSearchFieldRef.current) {
      setTimeout(() => {
        popoverSearchFieldRef.current?.focus();
      }, 0);
    }
  }, [isPopoverOpened]);

  const popoverWidth = popoverHandlerRef.current?.clientWidth ?? 200;

  useEffect(() => {
    findDefaultValueLabel();
  }, [findDefaultValueLabel]);

  useEffect(() => {
    if (!value && selectedOptionLabel) {
      setSelectedOptionLabel(undefined);
    }
  }, [value, selectedOptionLabel, setSelectedOptionLabel]);

  const context = useCmsClientContext();
  const staticText = getClientTranslations(BaseTranslations, context);
  const inputPlaceholder = staticText.policySearch_select_searchField;

  useEffect(() => {
    setSearchInputValue('');
    setOptionsToDisplay(options);
  }, [options]);

  useEffect(() => {
    focusPopoverSearchField();
  }, [focusPopoverSearchField]);

  return (
    <Popover
      open={isPopoverOpened}
      handler={togglePopover}
      placement="bottom-start"
    >
      <>
        {isPopoverOpened &&
          showBodyShadow &&
          createPortal(
            <div className="fixed top-0 size-full bg-black/30" />,
            document.body,
          )}
        <PopoverHandler ref={popoverHandlerRef}>
          <div className="size-full">
            <button
              className={classNames(
                'flex size-full flex-row items-center justify-between overflow-hidden rounded-3xl border border-gray-400 font-medium bg-white sm:px-4 md:px-2 lg:px-4',
                {
                  '!border-transparent': !border,
                  [`${disabledStyles}`]: disabled,
                  '!cursor-default pointer-events-none': isLoading,
                },
                {
                  [`${containerClassName}`]: Boolean(containerClassName),
                  [`${openedContainerClassName}`]:
                    Boolean(openedContainerClassName) && isPopoverOpened,
                },
              )}
              type="button"
            >
              <div
                className={classNames(
                  'flex w-full items-center gap-2 text-start',
                  { '!w-[90%]': showDropdownArrow },
                )}
              >
                {prefixIcon}
                <div
                  className={classNames(
                    'w-full font-medium text-gray-900 sm:text-base md:text-sm lg:text-lg',
                    {
                      [labelClassName ?? '']: Boolean(labelClassName),
                      '!text-gray-400': Boolean(
                        !selectedOptionLabel && placeholder,
                      ),
                    },
                  )}
                >
                  {selectedOptionLabel || placeholder}
                </div>
              </div>
              {showDropdownArrow && (
                <MaterialIcon
                  icon="expand_more"
                  className={classNames(
                    `text-gray-600 text-xl ${dropdownClassName}`,
                    { 'rotate-180': isPopoverOpened },
                  )}
                />
              )}
            </button>
          </div>
        </PopoverHandler>
        <PopoverContent
          // eslint-disable-next-line tailwindcss/no-arbitrary-value
          className={classNames(
            'z-[var(--z-index-dropdown)] overflow-hidden rounded-lg border border-gray-600 p-0 shadow-xl md:rounded-xl',
            { [`${popoverClassName}`]: Boolean(popoverClassName) },
          )}
        >
          <div
            className="flex max-h-96 w-full flex-col"
            style={{ width: popoverWidth }}
          >
            {search && (
              <div className="flex items-center rounded-none border-b border-b-gray-300 md:gap-1 md:px-2 lg:gap-2 lg:px-4">
                <div className="size-6 flex-none">
                  <MaterialIcon icon="search" className="text-gray-600" />
                </div>
                <input
                  className="w-full border-none py-2 pl-0 font-medium outline-none text-gray-600 text-base"
                  ref={popoverSearchFieldRef}
                  placeholder={inputPlaceholder}
                  value={searchInputValue}
                  onChange={handleSearchInputChange}
                />
              </div>
            )}
            <div className="flex max-h-70 flex-col overflow-y-auto px-4 py-2">
              {optionsToDisplay.map((option, index) => (
                <div
                  onClick={() => handleChange(option)}
                  role="button"
                  tabIndex={index}
                  className={classNames(
                    'flex cursor-pointer items-center justify-between rounded-lg hover:bg-gray-200 sm:p-3 md:p-2 lg:p-3',
                    {
                      'bg-gray-200': option.label === selectedOptionLabel,
                    },
                  )}
                  // eslint-disable-next-line react/no-array-index-key
                  key={`${option.value}_${index}`}
                  data-value={option.value}
                >
                  <p className="font-medium text-gray-900 text-lg">
                    {option.label}
                  </p>
                  {option.label === selectedOptionLabel && (
                    <div className="size-6">
                      <MaterialIcon icon="check" className="text-primary-400" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </PopoverContent>
      </>
    </Popover>
  );
};
